import React from 'react';
import { Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import StyledModal from '../Components/GenericModal/StyledModal';
import ModalFooterButtons from '../Components/GenericModal/ModalFooterButtons';
import { sharedClasses } from '../Components/CustomUIElements/sharedClasses';
import Api from './API';
import { Virtualize } from '../Components/CustomUIElements/VirtualizedAutocomplete';
import { styles } from './styles';
import { IJob } from './types';

interface JobSelectionDialogProps {
  selectedJob: IJob | undefined;
  setSelectedJob: (job: IJob | undefined) => void;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (job: IJob) => void;
}

interface ApiResponse {
  res: {
    jobs: IJob[];
  };
}

export default function JobSelectionDialog({
  selectedJob,
  setSelectedJob,
  isOpen,
  onClose,
  onUpdate
}: JobSelectionDialogProps) {
  const { data: jobs, isLoading } = useQuery({
    queryKey: ['jobs_for_tlp_match'],
    queryFn: async () => {
      const response = (await Api.getJobs()) as ApiResponse;
      return response.res.jobs;
    },
    initialData: []
  });

  const { data: jobDetails } = useQuery({
    queryKey: ['job_details', selectedJob?.id],
    queryFn: async () => {
      if (!selectedJob?.id) {
        return undefined;
      }

      const response = (await Api.getJobDetails(selectedJob?.id)) as ApiResponse;
      return response.res;
    }
  });

  const optionToString = (job: IJob) => `${job.reference} - ${job.title}`;
  const options = jobs.map((job) => optionToString(job));

  const handleSelectJob = (value: string) => {
    if (!value) {
      setSelectedJob(undefined);
      return;
    }
    const reference = value.split(' - ')[0];
    const job = jobs.find((job) => job.reference === reference);
    setSelectedJob(job);
  };

  const handleSubmit = () => {
    if (selectedJob) {
      onUpdate(selectedJob);
      onClose();
    }
  };

  return (
    <StyledModal
      isOpen={isOpen}
      handleClose={onClose}
      styleOverrides={{
        maxWidth: '700px',
        maxHeight: '500px'
      }}
      label="job-selection-dialog"
      modalTitle="Select Job"
      subHeaderStyling={false}
    >
      <Box sx={{ ...sharedClasses.mainModalContent, height: '100%' }}>
        <Box
          sx={{
            fontFamily: 'Source Sans Pro',
            fontSize: '16px',
            color: '#939393',
            width: '100%',
            paddingInline: '2px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <Virtualize
            passOptions={options}
            value={selectedJob ? optionToString(selectedJob) : ''}
            setValue={handleSelectJob}
            passedStyles={styles.autocomplete}
            loading={isLoading}
          />
        </Box>
        {jobDetails && (
          <Box sx={{ marginTop: '5px' }}>
            <Box dangerouslySetInnerHTML={{ __html: jobDetails.description }} />
            <Typography>Location: {jobDetails.location}</Typography>
          </Box>
        )}
      </Box>
      <ModalFooterButtons
        primaryButtonText="Update"
        primaryButtonCallback={handleSubmit}
        primaryButtonDisabled={!selectedJob}
        secondaryButtonText="Cancel"
        secondaryButtonCallback={onClose}
        primaryButtonID="select-job-button"
        secondaryButtonID="cancel-job-selection"
      />
    </StyledModal>
  );
} 
